/**
 * The words `portal-kit` itself puts on a screen.
 *
 * Two kinds of thing, and the split is by *who owns the screen*. A report's age
 * (§39.3a), the provisional label (§39.4) and a conversion's provenance (§21.7b)
 * are what the shared presentation layer writes on its own behalf — all three
 * consoles render them identically and none of them owns one. Beneath those sit
 * the screens `portal-kit` renders whole: sign in, the two §3.7 onboarding
 * steps, the staff list and the shared controls. A string on one of those is
 * not any console's to translate either, so it is here rather than copied into
 * three catalogues and corrected in one of them.
 *
 * Plurals are separate messages rather than a rule, because a rule that knows
 * English, Spanish and Brazilian Portuguese all use one/other is a rule that is
 * wrong the first time somebody adds Polish. Two keys is the honest amount of
 * machinery for the three languages shipped, and the fourth language changes
 * this file rather than surprising it.
 *
 * §2.8b: nothing here is a status, a reason code or a currency. The tests in
 * `test/i18n.test.ts` hold that for every catalogue, this one included.
 */
import { type Catalogue, type Params, type Translator, tabTranslator } from './i18n.ts';

export const KIT_MESSAGES = {
  'freshness.now': {
    en: 'Updated just now',
    es: 'Actualizado ahora mismo',
    'pt-BR': 'Atualizado agora mesmo',
  },
  'freshness.minutes.one': {
    en: 'Updated {n} minute ago',
    es: 'Actualizado hace {n} minuto',
    'pt-BR': 'Atualizado há {n} minuto',
  },
  'freshness.minutes.other': {
    en: 'Updated {n} minutes ago',
    es: 'Actualizado hace {n} minutos',
    'pt-BR': 'Atualizado há {n} minutos',
  },
  'freshness.hours.one': {
    en: 'Updated {n} hour ago',
    es: 'Actualizado hace {n} hora',
    'pt-BR': 'Atualizado há {n} hora',
  },
  'freshness.hours.other': {
    en: 'Updated {n} hours ago',
    es: 'Actualizado hace {n} horas',
    'pt-BR': 'Atualizado há {n} horas',
  },
  'freshness.days.one': {
    en: 'Updated {n} day ago',
    es: 'Actualizado hace {n} día',
    'pt-BR': 'Atualizado há {n} dia',
  },
  'freshness.days.other': {
    en: 'Updated {n} days ago',
    es: 'Actualizado hace {n} días',
    'pt-BR': 'Atualizado há {n} dias',
  },
  /*
   * §39.4 — the fallback wording only. When the server explains *why* a figure
   * is provisional it sends its own sentence, and that sentence arrives in
   * English: API prose is not localised yet, and pretending otherwise by
   * translating the default around it would be worse than the gap it hides.
   */
  'figure.provisional': {
    en: 'This figure is provisional and may change until the period is locked.',
    es: 'Esta cifra es provisional y puede cambiar hasta que el período quede bloqueado.',
    'pt-BR': 'Este valor é provisório e pode mudar até que o período seja bloqueado.',
  },

  /* ---------------------------------------------------------------- *
   * §21.7b — the provenance of a converted figure, in words
   *
   * Here rather than in a console catalogue because all three render it
   * identically and none of them owns it, which is the same argument the
   * freshness strings above were moved for. The rate's `source` is not
   * translated: it arrives from the server as the name of a rate publisher,
   * and translating the name of an institution is how a reader stops being
   * able to look it up.
   * ---------------------------------------------------------------- */

  /** Beside the figure. Short, because it sits next to a number in a card. */
  'converted.mark': { en: 'Converted', es: 'Convertido', 'pt-BR': 'Convertido' },
  'converted.mark.numbered': {
    en: 'Converted ({marker})',
    es: 'Convertido ({marker})',
    'pt-BR': 'Convertido ({marker})',
  },
  /*
   * §21.7b — a figure whose rows did not all use the same rate says so where
   * the figure is, not only in the footnote. "Converted" beside it and one
   * rate underneath would tell the reader this number came from that rate,
   * which is the thing that is not true.
   */
  /* A note the server sent for a figure that was already in the target. */
  'converted.mark.identity': {
    en: 'Already in this currency',
    es: 'Ya está en esta moneda',
    'pt-BR': 'Já está nesta moeda',
  },
  'converted.mark.mixed': {
    en: 'Converted at more than one rate',
    es: 'Convertido a más de un tipo de cambio',
    'pt-BR': 'Convertido a mais de uma taxa',
  },
  'converted.mark.mixed.numbered': {
    en: 'Converted at more than one rate ({marker})',
    es: 'Convertido a más de un tipo de cambio ({marker})',
    'pt-BR': 'Convertido a mais de uma taxa ({marker})',
  },

  'converted.summary.frozen': {
    en:
      'Converted to {currency} at the rate frozen when the period was locked, ' +
      'so this figure does not move.',
    es:
      'Convertido a {currency} con el tipo de cambio fijado al bloquear el ' +
      'período, así que esta cifra no cambia.',
    'pt-BR':
      'Convertido para {currency} pela taxa congelada quando o período foi ' +
      'bloqueado, então este valor não muda.',
  },
  'converted.summary.latest': {
    en:
      'Converted to {currency} at the most recent published rate. It can move ' +
      'until the period is locked.',
    es:
      'Convertido a {currency} con el tipo de cambio publicado más reciente. ' +
      'Puede cambiar hasta que el período quede bloqueado.',
    'pt-BR':
      'Convertido para {currency} pela taxa publicada mais recente. Pode mudar ' +
      'até que o período seja bloqueado.',
  },
  'converted.summary.average': {
    en:
      'Converted to {currency} at an average of the rates published inside the ' +
      'period, which is what this operator asked for.',
    es:
      'Convertido a {currency} con el promedio de los tipos de cambio publicados ' +
      'dentro del período, que es lo que pidió este operador.',
    'pt-BR':
      'Convertido para {currency} pela média das taxas publicadas dentro do ' +
      'período, que é o que este operador pediu.',
  },
  'converted.summary.identity': {
    en: 'Already in {currency}. No rate was applied.',
    es: 'Ya está en {currency}. No se aplicó ningún tipo de cambio.',
    'pt-BR': 'Já está em {currency}. Nenhuma taxa foi aplicada.',
  },
  'converted.summary.several': {
    en:
      'Converted to {currency} from {n} currencies, each at its own rate. All ' +
      'of them are below.',
    es:
      'Convertido a {currency} desde {n} monedas, cada una con su propio tipo ' +
      'de cambio. Están todos abajo.',
    'pt-BR':
      'Convertido para {currency} a partir de {n} moedas, cada uma com sua ' +
      'própria taxa. Todas estão abaixo.',
  },
  'converted.summary.mixed': {
    en:
      'Converted to {currency}, and the rows behind it did not all use the ' +
      'same kind of rate — some were frozen at a period lock and some are the ' +
      'latest published. No single rate produced this figure. Every rate used ' +
      'is below.',
    es:
      'Convertido a {currency}, y no todas las filas que hay detrás usaron el ' +
      'mismo tipo de cambio: unas quedaron fijadas al bloquear un período y ' +
      'otras son la última publicada. Ningún tipo de cambio por sí solo produjo ' +
      'esta cifra. Abajo están todos los que se usaron.',
    'pt-BR':
      'Convertido para {currency}, e as linhas por trás dele não usaram todas o ' +
      'mesmo tipo de taxa — algumas foram congeladas no bloqueio de um período ' +
      'e outras são a última publicada. Nenhuma taxa sozinha produziu este ' +
      'valor. Todas as taxas usadas estão abaixo.',
  },

  /* One rate, written out: the rate itself, its source, its as_of, its policy. */
  'converted.rate.line': {
    en: '1 {from} = {rate} {to} — {source}, as of {asOf}. {policy}',
    es: '1 {from} = {rate} {to} — {source}, a fecha de {asOf}. {policy}',
    'pt-BR': '1 {from} = {rate} {to} — {source}, em {asOf}. {policy}',
  },
  'converted.rate.line.kind': {
    en: '1 {from} = {rate} {to} — {source} ({kind}), as of {asOf}. {policy}',
    es: '1 {from} = {rate} {to} — {source} ({kind}), a fecha de {asOf}. {policy}',
    'pt-BR': '1 {from} = {rate} {to} — {source} ({kind}), em {asOf}. {policy}',
  },
  'converted.policy.frozen': {
    en: 'Frozen when the period was locked.',
    es: 'Fijado al bloquear el período.',
    'pt-BR': 'Congelada quando o período foi bloqueado.',
  },
  'converted.policy.latest': {
    en: 'The latest published rate, which can still move.',
    es: 'El tipo de cambio publicado más reciente, que aún puede cambiar.',
    'pt-BR': 'A taxa publicada mais recente, que ainda pode mudar.',
  },
  'converted.policy.average': {
    en: 'An average of the rates published inside the period.',
    es: 'Un promedio de los tipos de cambio publicados dentro del período.',
    'pt-BR': 'Uma média das taxas publicadas dentro do período.',
  },
  'converted.policy.identity': {
    en: 'No conversion was applied.',
    es: 'No se aplicó ninguna conversión.',
    'pt-BR': 'Nenhuma conversão foi aplicada.',
  },
  /*
   * §21.7b — what the operator asked for, beside what this figure got.
   *
   * Both, because they disagree legitimately: a settled period takes the rate
   * frozen when it locked whatever the timing setting says (§21.7c), and a
   * reader shown only the outcome cannot tell a rule from a setting being
   * ignored.
   */
  'converted.timing.transaction': {
    en: 'Your operator’s setting takes the rate in force at the time of the transaction.',
    es: 'La configuración de tu operador toma el tipo de cambio vigente en el momento de la transacción.',
    'pt-BR': 'A configuração do seu operador usa a taxa vigente no momento da transação.',
  },
  'converted.timing.periodEnd': {
    en: 'Your operator’s setting takes the rate as of the end of the period being reported.',
    es: 'La configuración de tu operador toma el tipo de cambio al final del período informado.',
    'pt-BR': 'A configuração do seu operador usa a taxa no fim do período informado.',
  },
  'converted.timing.periodAverage': {
    en: 'Your operator’s setting averages the rates published inside the period.',
    es: 'La configuración de tu operador promedia los tipos de cambio publicados dentro del período.',
    'pt-BR': 'A configuração do seu operador calcula a média das taxas publicadas dentro do período.',
  },
  /* §21.3b — whose rate it was, when the wire still carries it. */
  'converted.kind.brand': {
    en: 'the brand’s own rate',
    es: 'el tipo de cambio de la propia marca',
    'pt-BR': 'a taxa da própria marca',
  },
  'converted.kind.operator': {
    en: 'the operator’s rate provider',
    es: 'el proveedor de tipos de cambio del operador',
    'pt-BR': 'o provedor de taxas do operador',
  },
  'converted.kind.platform': {
    en: 'the platform default rate',
    es: 'el tipo de cambio predeterminado de la plataforma',
    'pt-BR': 'a taxa padrão da plataforma',
  },
  'converted.kind.identity': {
    en: 'the same currency',
    es: 'la misma moneda',
    'pt-BR': 'a mesma moeda',
  },

  /* ---------------------------------------------------------------- *
   * §21.7 — which of the two views the reader is in, and how to leave it
   *
   * The wording follows E37's correction to the language picker: a state with
   * no control back to it is described, never offered. Where there is a
   * control it is labelled with what it will do, and where there is not the
   * screen says what is happening instead.
   * ---------------------------------------------------------------- */

  'currencyView.showing.converted': {
    en: 'Showing every figure converted to {currency}.',
    es: 'Mostrando todas las cifras convertidas a {currency}.',
    'pt-BR': 'Mostrando todos os valores convertidos para {currency}.',
  },
  'currencyView.showing.original': {
    en: 'Showing every figure in the currency it was computed in.',
    es: 'Mostrando cada cifra en la moneda en que se calculó.',
    'pt-BR': 'Mostrando cada valor na moeda em que foi calculado.',
  },
  /*
   * The reader asked for {currency} and got no conversion, which is not a
   * failure and is not the same as being in the original view by choice. Said,
   * because a screen that stays silent here reads as "nothing needed saying".
   */
  'currencyView.showing.nothing': {
    en:
      'Nothing on this screen was converted: every figure is already in ' +
      '{currency}, or is one that cannot be converted.',
    es:
      'Nada en esta pantalla se convirtió: cada cifra ya está en {currency}, o ' +
      'es una que no se puede convertir.',
    'pt-BR':
      'Nada nesta tela foi convertido: cada valor já está em {currency}, ou é ' +
      'um valor que não pode ser convertido.',
  },
  /*
   * The reader's currency is the one the report adds up in anyway, so it moved
   * nothing — but a total over more than one currency was still converted to
   * be added up at all. Not "nothing was converted", which would be untrue
   * beside the rate the screen is also showing.
   */
  'currencyView.showing.addedUp': {
    en:
      'Every figure is in {currency}, which is also the currency these figures ' +
      'are added up in, so the original-currency view would show exactly this. ' +
      'Amounts in other currencies are converted into it, because a total or a ' +
      'row holding more than one currency can only be shown in one.',
    es:
      'Todas las cifras están en {currency}, que es también la moneda en que se ' +
      'suman, así que la vista en la moneda original mostraría exactamente ' +
      'esto. Los importes en otras monedas se convierten a ella, porque un ' +
      'total o una fila con más de una moneda solo puede mostrarse en una.',
    'pt-BR':
      'Todos os valores estão em {currency}, que também é a moeda em que eles ' +
      'são somados, então a visualização na moeda original mostraria ' +
      'exatamente isto. Os valores em outras moedas são convertidos para ela, ' +
      'porque um total ou uma linha com mais de uma moeda só pode ser mostrado ' +
      'em uma.',
  },
  /* The original view of a total or row over more than one currency, said as it is. */
  'currencyView.showing.originalAddedUp': {
    en:
      'Showing the figures as computed. A total or a row holding more than one ' +
      'currency can only be shown in one, so amounts in other currencies are ' +
      'converted into {currency}.',
    es:
      'Mostrando las cifras tal como se calcularon. Un total o una fila con más ' +
      'de una moneda solo puede mostrarse en una, así que los importes en otras ' +
      'monedas se convierten a {currency}.',
    'pt-BR':
      'Mostrando os valores como foram calculados. Um total ou uma linha com ' +
      'mais de uma moeda só pode ser mostrado em uma, então os valores em ' +
      'outras moedas são convertidos para {currency}.',
  },
  'currencyView.action.original': {
    en: 'Show the original currencies',
    es: 'Ver las monedas originales',
    'pt-BR': 'Ver as moedas originais',
  },
  'currencyView.action.converted': {
    en: 'Show the figures in {currency}',
    es: 'Ver las cifras en {currency}',
    'pt-BR': 'Ver os valores em {currency}',
  },
  /*
   * §21.7 with nothing chosen — E37's shape. It says which state the reader is
   * in rather than offering "use the operator default", which is not a control
   * this screen has.
   */
  'currencyView.inherited': {
    en:
      'You have not chosen a currency, so this follows your operator’s default ' +
      '({currency}).',
    es:
      'No has elegido moneda, así que esto sigue el predeterminado de tu ' +
      'operador ({currency}).',
    'pt-BR':
      'Você não escolheu uma moeda, então isto segue o padrão do seu operador ' +
      '({currency}).',
  },
  /*
   * §21.7f — a billing document is not restated.
   *
   * Shown only to a reader who set a display currency, because it answers a
   * question only they have: the reports moved and these figures did not. To
   * anybody else it is a paragraph about something that never happened.
   */
  'billing.notConverted': {
    en:
      'These are shown in the currency each document was billed in, and are ' +
      'not converted to {currency}. A document is paid against the amount it ' +
      'states.',
    es:
      'Se muestran en la moneda en que se facturó cada documento y no se ' +
      'convierten a {currency}. Un documento se paga por el importe que indica.',
    'pt-BR':
      'Estes aparecem na moeda em que cada documento foi faturado e não são ' +
      'convertidos para {currency}. Um documento é pago pelo valor que ele ' +
      'declara.',
  },
  /* Only where there is something to switch — see `currencyViewControl`. */
  'currencyView.temporary': {
    en: 'Switching here changes this screen, and only while you are on it.',
    es: 'Cambiar aquí afecta a esta pantalla, y solo mientras estés en ella.',
    'pt-BR': 'Alternar aqui afeta esta tela, e apenas enquanto você estiver nela.',
  },

  /* ================================================================ *
   * The screens `portal-kit` owns outright — §2.8a and §2.8f.
   *
   * Sign-in, the two onboarding steps and the staff list are not any one
   * console's screens: all three render them from here, so the words are
   * here too. Everything below this line was English in every language
   * until now, which is the half of §2.8 a mechanism does not satisfy on
   * its own — a picker that changes the rail and leaves the form it is
   * sitting on in English is the control E37 already called worse than
   * none.
   *
   * §2.8b holds unchanged: a role *identifier* (`FINANCE`) is data and is
   * never translated; the words a screen prints for it are not.
   * ================================================================ */

  /* ---------------------------------------------------------------- *
   * Field labels and verbs shared by more than one of those screens
   * ---------------------------------------------------------------- */
  'field.email': { en: 'Email', es: 'Correo electrónico', 'pt-BR': 'E-mail' },
  'field.password': { en: 'Password', es: 'Contraseña', 'pt-BR': 'Senha' },
  'field.yourPassword': { en: 'Your password', es: 'Tu contraseña', 'pt-BR': 'Sua senha' },
  'field.currentPassword': {
    en: 'Current password',
    es: 'Contraseña actual',
    'pt-BR': 'Senha atual',
  },
  'field.newPassword': { en: 'New password', es: 'Nueva contraseña', 'pt-BR': 'Nova senha' },
  // The second box on a change-password form. "Again" is the whole label in
  // English; neither other language has a one-word imperative that reads as a
  // field label, so both say what to do again.
  'field.again': { en: 'Again', es: 'Repítela', 'pt-BR': 'Repita' },
  'field.code': {
    en: 'Code from your authenticator app',
    es: 'Código de tu app de autenticación',
    'pt-BR': 'Código do seu app autenticador',
  },
  'field.code.hint': {
    en: 'Six digits. It changes every 30 seconds.',
    es: 'Seis dígitos. Cambia cada 30 segundos.',
    'pt-BR': 'Seis dígitos. Muda a cada 30 segundos.',
  },

  'action.cancel': { en: 'Cancel', es: 'Cancelar', 'pt-BR': 'Cancelar' },
  'action.confirm': { en: 'Confirm', es: 'Confirmar', 'pt-BR': 'Confirmar' },
  'action.close': { en: 'Close', es: 'Cerrar', 'pt-BR': 'Fechar' },
  /* On a dashboard card that opens the working behind its figure. */
  'figure.open': { en: 'See the detail', es: 'Ver el detalle', 'pt-BR': 'Ver o detalhe' },
  'map.zoom': { en: 'Zoom', es: 'Zoom', 'pt-BR': 'Zoom' },
  'map.zoomIn': { en: 'Zoom in', es: 'Acercar', 'pt-BR': 'Aproximar' },
  'map.zoomOut': { en: 'Zoom out', es: 'Alejar', 'pt-BR': 'Afastar' },
  'action.search': { en: 'Search', es: 'Buscar', 'pt-BR': 'Buscar' },
  'action.checking': { en: 'Checking…', es: 'Comprobando…', 'pt-BR': 'Verificando…' },
  'action.saving': { en: 'Saving…', es: 'Guardando…', 'pt-BR': 'Salvando…' },
  'action.starting': { en: 'Starting…', es: 'Iniciando…', 'pt-BR': 'Iniciando…' },
  'action.working': { en: 'Working…', es: 'Procesando…', 'pt-BR': 'Processando…' },
  'action.adding': { en: 'Adding…', es: 'Añadiendo…', 'pt-BR': 'Adicionando…' },
  'action.removing': { en: 'Removing…', es: 'Eliminando…', 'pt-BR': 'Removendo…' },

  /* ---------------------------------------------------------------- *
   * Sign in (§3.7)
   * ---------------------------------------------------------------- */
  'signin.panel': { en: 'Sign in', es: 'Iniciar sesión', 'pt-BR': 'Entrar' },
  // §3.7's second factor is a step, not an error — the panel title carries
  // that, so it must not become a word like "Error" in translation.
  'signin.panel.code': { en: 'One more step', es: 'Un paso más', 'pt-BR': 'Mais um passo' },
  'signin.verify': { en: 'Verify', es: 'Verificar', 'pt-BR': 'Verificar' },
  'signin.createAccount': {
    en: 'Create an account',
    es: 'Crear una cuenta',
    'pt-BR': 'Criar uma conta',
  },
  'signin.unreachable': {
    en: 'Could not reach the server. Check your connection and try again.',
    es: 'No se pudo conectar con el servidor. Revisa tu conexión e inténtalo de nuevo.',
    'pt-BR': 'Não foi possível acessar o servidor. Verifique sua conexão e tente novamente.',
  },
  'signout.busy': { en: 'Signing out…', es: 'Cerrando sesión…', 'pt-BR': 'Saindo…' },
  'signout.action': { en: 'Sign out', es: 'Cerrar sesión', 'pt-BR': 'Sair' },

  /* ---------------------------------------------------------------- *
   * Enrolling a second factor (§3.7)
   * ---------------------------------------------------------------- */
  'onboard.title': {
    en: 'Set up your authenticator',
    es: 'Configura tu app de autenticación',
    'pt-BR': 'Configure seu app autenticador',
  },
  'onboard.panel': {
    en: 'One-time setup',
    es: 'Configuración inicial',
    'pt-BR': 'Configuração única',
  },
  'onboard.intro': {
    en:
      'Everyone with an internal role needs an authenticator app. It is not ' +
      'something an administrator can turn off — the second factor is what ' +
      'stops your password alone being enough.',
    es:
      'Todo el personal interno necesita una app de autenticación. Un ' +
      'administrador no puede desactivarla: el segundo factor es lo que impide ' +
      'que tu contraseña baste por sí sola.',
    'pt-BR':
      'Todo mundo com um papel interno precisa de um app autenticador. Não é ' +
      'algo que um administrador possa desligar — o segundo fator é o que ' +
      'impede que só a sua senha baste.',
  },
  'onboard.badPassword': {
    en: 'That password was not accepted. Check it and try again.',
    es: 'Esa contraseña no se aceptó. Revísala e inténtalo de nuevo.',
    'pt-BR': 'Essa senha não foi aceita. Confira e tente novamente.',
  },
  'onboard.startFailed': {
    en: 'Could not start setup. Try signing in again.',
    es: 'No se pudo iniciar la configuración. Vuelve a iniciar sesión.',
    'pt-BR': 'Não foi possível iniciar a configuração. Entre novamente.',
  },
  'onboard.codeMismatch': {
    en: 'That code did not match. Try the next one.',
    es: 'Ese código no coincide. Prueba con el siguiente.',
    'pt-BR': 'Esse código não confere. Tente o próximo.',
  },
  'onboard.start': { en: 'Start setup', es: 'Empezar', 'pt-BR': 'Iniciar configuração' },
  'onboard.key': { en: 'Setup key', es: 'Clave de configuración', 'pt-BR': 'Chave de configuração' },
  'onboard.key.hint': {
    en: 'Scan the QR code above, or type this key into your authenticator app.',
    es: 'Escanea el código QR de arriba, o escribe esta clave en tu app de autenticación.',
    'pt-BR': 'Escaneie o QR code acima, ou digite esta chave no seu app autenticador.',
  },
  'onboard.code': { en: 'Code from the app', es: 'Código de la app', 'pt-BR': 'Código do app' },
  'onboard.finish': { en: 'Finish setup', es: 'Finalizar', 'pt-BR': 'Concluir configuração' },

  /* ---------------------------------------------------------------- *
   * Changing a password (§3.7)
   * ---------------------------------------------------------------- */
  'password.title': { en: 'Choose a password', es: 'Elige una contraseña', 'pt-BR': 'Escolha uma senha' },
  'password.panel': { en: 'Before you continue', es: 'Antes de continuar', 'pt-BR': 'Antes de continuar' },
  'password.intro': {
    en:
      'The password you were given is one two people know — you and whoever ' +
      'set up your account. It has to change before you can do anything else.',
    es:
      'La contraseña que te dieron la conocen dos personas: tú y quien creó tu ' +
      'cuenta. Tiene que cambiar antes de que puedas hacer cualquier otra cosa.',
    'pt-BR':
      'A senha que você recebeu é conhecida por duas pessoas — você e quem ' +
      'criou a sua conta. Ela precisa mudar antes de qualquer outra coisa.',
  },
  'password.mismatch': { en: 'These do not match.', es: 'No coinciden.', 'pt-BR': 'Não coincidem.' },
  'password.failed': { en: 'That did not work.', es: 'No funcionó.', 'pt-BR': 'Não funcionou.' },
  'password.unreachable': {
    en: 'Could not change it. Check your connection and try again.',
    es: 'No se pudo cambiar. Revisa tu conexión e inténtalo de nuevo.',
    'pt-BR': 'Não foi possível alterar. Verifique sua conexão e tente novamente.',
  },
  'password.submit': { en: 'Set password', es: 'Guardar contraseña', 'pt-BR': 'Definir senha' },
  // §3.7's whole policy is length, so this is the whole checklist. `{n}` rather
  // than a written-out twelve, because the minimum is a constant the server
  // owns and a translated numeral is one more place it can go stale.
  'password.rule.length': {
    en: 'At least {n} characters',
    es: 'Al menos {n} caracteres',
    'pt-BR': 'Pelo menos {n} caracteres',
  },
  'password.rule.maxLength': {
    en: 'At most {n} characters',
    es: 'Como máximo {n} caracteres',
    'pt-BR': 'No máximo {n} caracteres',
  },
  // Why api-core refused a new password (WEAK_PASSWORD, details.reason).
  'password.refused.TOO_SHORT': {
    en: 'Use at least {min} characters.',
    es: 'Usa al menos {min} caracteres.',
    'pt-BR': 'Use pelo menos {min} caracteres.',
  },
  'password.refused.TOO_LONG': {
    en: 'Use at most {max} characters.',
    es: 'Usa como máximo {max} caracteres.',
    'pt-BR': 'Use no máximo {max} caracteres.',
  },
  'password.refused.COMMON': {
    en: 'That password is one of the most commonly used, so it is among the first anyone guessing would try. Choose another.',
    es: 'Esa contraseña es de las más usadas, así que es de las primeras que probaría quien intente adivinarla. Elige otra.',
    'pt-BR': 'Essa senha está entre as mais usadas, então é uma das primeiras que alguém tentaria adivinhar. Escolha outra.',
  },
  'password.refused.PERSONAL': {
    en: 'A password must not contain your email address or your business name.',
    es: 'La contraseña no puede contener tu dirección de correo ni el nombre de tu empresa.',
    'pt-BR': 'A senha não pode conter seu endereço de e-mail nem o nome da sua empresa.',
  },
  'password.rule.match': {
    en: 'Both entries match',
    es: 'Las dos coinciden',
    'pt-BR': 'As duas coincidem',
  },
  // Read out instead of the tick, which is decorative. The leading space is
  // part of the message: it follows the rule's label in the same breath.
  'password.rule.met': { en: ' — met', es: ' — cumplido', 'pt-BR': ' — cumprido' },
  'password.rule.notMet': {
    en: ' — not yet met',
    es: ' — aún no cumplido',
    'pt-BR': ' — ainda não cumprido',
  },

  /* ---------------------------------------------------------------- *
   * The shared controls in `ui.tsx`
   * ---------------------------------------------------------------- */
  'ui.loading': { en: 'Loading', es: 'Cargando', 'pt-BR': 'Carregando' },
  // A folded note (Callout) and the way to read the rest of it.
  'ui.showMore': { en: 'Show more', es: 'Ver más', 'pt-BR': 'Ver mais' },
  'ui.showLess': { en: 'Show less', es: 'Ver menos', 'pt-BR': 'Ver menos' },
  'ui.search.type': { en: 'Type to search…', es: 'Escribe para buscar…', 'pt-BR': 'Digite para buscar…' },
  'ui.search.ellipsis': { en: 'Search…', es: 'Buscar…', 'pt-BR': 'Buscar…' },
  'ui.copy': { en: 'Copy', es: 'Copiar', 'pt-BR': 'Copiar' },
  'ui.copied': { en: 'Copied', es: 'Copiado', 'pt-BR': 'Copiado' },
  'ui.copyFailed': { en: 'Copy failed', es: 'No se pudo copiar', 'pt-BR': 'Falha ao copiar' },
  'ui.retry': { en: 'Try again', es: 'Reintentar', 'pt-BR': 'Tentar novamente' },
  /* §0.8d — the rule id under a failure's sentence, so it can be read back to
     support instead of re-described from memory. */
  'ui.file.choose': { en: 'Choose a file', es: 'Elegir un archivo', 'pt-BR': 'Escolher um arquivo' },
  'ui.file.none': {
    en: 'No file chosen yet',
    es: 'Ningún archivo elegido todavía',
    'pt-BR': 'Nenhum arquivo escolhido ainda',
  },
  // §33.4 — files sent with a dispute, either side's.
  'ui.attach.add': { en: 'Attach a file', es: 'Adjuntar un archivo', 'pt-BR': 'Anexar um arquivo' },
  'ui.attach.another': { en: 'Attach another', es: 'Adjuntar otro', 'pt-BR': 'Anexar outro' },
  'ui.attach.count': {
    en: '{n} file(s) attached',
    es: '{n} archivo(s) adjunto(s)',
    'pt-BR': '{n} arquivo(s) anexado(s)',
  },
  'ui.attach.remove': { en: 'Remove', es: 'Quitar', 'pt-BR': 'Remover' },
  'ui.attach.hint': {
    en: 'Up to 5 files, 10 MB each: images, PDF, video, CSV or plain text.',
    es: 'Hasta 5 archivos de 10 MB cada uno: imágenes, PDF, vídeo, CSV o texto.',
    'pt-BR': 'Até 5 arquivos de 10 MB cada: imagens, PDF, vídeo, CSV ou texto.',
  },
  'ui.attach.type': {
    en: '"{name}" is not a type that can be attached. Images, PDF, video, CSV and plain text can.',
    es: '"{name}" no es un tipo que se pueda adjuntar. Se pueden imágenes, PDF, vídeo, CSV y texto.',
    'pt-BR': '"{name}" não é um tipo que possa ser anexado. Imagens, PDF, vídeo, CSV e texto podem.',
  },
  'ui.attach.empty': { en: '"{name}" is empty.', es: '"{name}" está vacío.', 'pt-BR': '"{name}" está vazio.' },
  'ui.attach.tooLarge': {
    en: '"{name}" is over 10 MB, the most one file can be.',
    es: '"{name}" supera los 10 MB, el máximo para un archivo.',
    'pt-BR': '"{name}" passa de 10 MB, o máximo para um arquivo.',
  },
  'ui.attach.tooMuch': {
    en: 'Together these files would be over 25 MB, the most one request can carry.',
    es: 'Juntos, estos archivos superarían los 25 MB, el máximo por envío.',
    'pt-BR': 'Juntos, estes arquivos passariam de 25 MB, o máximo por envio.',
  },
  'ui.attach.unreadable': {
    en: '"{name}" could not be read.',
    es: 'No se pudo leer "{name}".',
    'pt-BR': 'Não foi possível ler "{name}".',
  },
  // What the platform recorded when a dispute was raised.
  'ui.ctx.title': {
    en: 'On record when it was raised',
    es: 'Registrado al presentarla',
    'pt-BR': 'Registrado quando foi aberta',
  },
  'ui.ctx.source': { en: 'Raised from', es: 'Presentada desde', 'pt-BR': 'Aberta a partir de' },
  'ui.ctx.source.CLICK_DISCREPANCY': {
    en: 'The click-discrepancy report',
    es: 'El informe de discrepancia de clics',
    'pt-BR': 'O relatório de discrepância de cliques',
  },
  'ui.ctx.source.DISPUTES_FORM': { en: 'The disputes form', es: 'El formulario de disputas', 'pt-BR': 'O formulário de contestações' },
  'ui.ctx.source.API': { en: 'The API', es: 'La API', 'pt-BR': 'A API' },
  'ui.ctx.captured': { en: 'Recorded', es: 'Registrado', 'pt-BR': 'Registrado' },
  'ui.ctx.range': { en: 'Report dates', es: 'Fechas del informe', 'pt-BR': 'Datas do relatório' },
  'ui.ctx.campaign': { en: 'Campaign', es: 'Campaña', 'pt-BR': 'Campanha' },
  'ui.ctx.product': { en: 'Product', es: 'Producto', 'pt-BR': 'Produto' },
  'ui.ctx.period': { en: 'Period', es: 'Periodo', 'pt-BR': 'Período' },
  'ui.ctx.fig.ours_total': { en: 'Clicks we recorded', es: 'Clics registrados', 'pt-BR': 'Cliques registrados' },
  'ui.ctx.fig.ours_valid': { en: 'Valid', es: 'Válidos', 'pt-BR': 'Válidos' },
  'ui.ctx.fig.bot': { en: 'Bot', es: 'Bot', 'pt-BR': 'Bot' },
  'ui.ctx.fig.duplicate': { en: 'Duplicate', es: 'Duplicados', 'pt-BR': 'Duplicados' },
  'ui.ctx.fig.geo_blocked': { en: 'Blocked by country', es: 'Bloqueados por país', 'pt-BR': 'Bloqueados por país' },
  'ui.ctx.fig.os_blocked': { en: 'Blocked by OS', es: 'Bloqueados por SO', 'pt-BR': 'Bloqueados por SO' },
  'ui.ctx.fig.device_blocked': { en: 'Blocked by device', es: 'Bloqueados por dispositivo', 'pt-BR': 'Bloqueados por dispositivo' },
  'ui.ctx.fig.targeting_blocked': { en: 'Blocked by targeting', es: 'Bloqueados por segmentación', 'pt-BR': 'Bloqueados por segmentação' },
  'ui.ctx.fig.affiliate_tracker_total': {
    en: 'The affiliate\'s own tracker',
    es: 'El rastreador del afiliado',
    'pt-BR': 'O rastreador do afiliado',
  },
  'ui.ctx.fig.gap_vs_valid': { en: 'Gap against valid', es: 'Diferencia con válidos', 'pt-BR': 'Diferença para válidos' },
  'ui.ctx.fig.explained_rejects': { en: 'Explained by rejections', es: 'Explicada por rechazos', 'pt-BR': 'Explicada por rejeições' },
  'ui.ctx.fig.unexplained_gap': { en: 'Unexplained gap', es: 'Diferencia sin explicar', 'pt-BR': 'Diferença sem explicação' },
  'ui.ctx.subIds': { en: 'Busiest sub-IDs', es: 'Sub-IDs con más tráfico', 'pt-BR': 'Sub-IDs com mais tráfego' },
  'ui.ctx.col.subId': { en: 'Sub-ID', es: 'Sub-ID', 'pt-BR': 'Sub-ID' },
  'ui.ctx.commission': {
    en: 'Commission in that period',
    es: 'Comisión en ese periodo',
    'pt-BR': 'Comissão nesse período',
  },
  'ui.ctx.col.type': { en: 'Type', es: 'Tipo', 'pt-BR': 'Tipo' },
  'ui.ctx.col.lines': { en: 'Lines', es: 'Líneas', 'pt-BR': 'Linhas' },
  'ui.ctx.col.amount': { en: 'Amount', es: 'Importe', 'pt-BR': 'Valor' },
  'ui.ctx.note': {
    en: 'Recorded by the platform when this was raised. The same report today may read differently — late clicks arrive and traffic is reclassified.',
    es: 'Registrado por la plataforma al presentarla. El mismo informe hoy puede dar otras cifras: llegan clics tardíos y el tráfico se reclasifica.',
    'pt-BR': 'Registrado pela plataforma quando foi aberta. O mesmo relatório hoje pode mostrar outros números: cliques atrasados chegam e o tráfego é reclassificado.',
  },
  'ui.password.show': { en: 'Show password', es: 'Mostrar contraseña', 'pt-BR': 'Mostrar senha' },
  'ui.password.hide': { en: 'Hide password', es: 'Ocultar contraseña', 'pt-BR': 'Ocultar senha' },
  'ui.landing': { en: 'Landing page', es: 'Página de destino', 'pt-BR': 'Página de destino' },
  'ui.landing.none': {
    en: 'No page on file for this brand — traffic uses the brand default URL.',
    es: 'No hay ninguna página registrada para esta marca: el tráfico usa la URL predeterminada de la marca.',
    'pt-BR': 'Não há página registrada para esta marca — o tráfego usa a URL padrão da marca.',
  },
  'ui.landing.hint': { en: 'Where the click lands.', es: 'Dónde aterriza el clic.', 'pt-BR': 'Onde o clique chega.' },
  'ui.landing.pick': {
    en: 'Pick where the click lands, or use the brand default.',
    es: 'Elige dónde aterriza el clic, o usa el valor predeterminado de la marca.',
    'pt-BR': 'Escolha onde o clique chega, ou use o padrão da marca.',
  },
  'ui.brandDefault': {
    en: 'Brand default',
    es: 'Predeterminado de la marca',
    'pt-BR': 'Padrão da marca',
  },

  /* ---------------------------------------------------------------- *
   * The build with no API address
   * ---------------------------------------------------------------- */
  'notConfigured.title': {
    en: 'This build has no API address',
    es: 'Esta compilación no tiene dirección de API',
    'pt-BR': 'Esta build não tem endereço de API',
  },
  'notConfigured.panel': { en: 'Nothing will load', es: 'No se cargará nada', 'pt-BR': 'Nada vai carregar' },
  // The variable name is a parameter rather than part of the sentence: it is an
  // identifier (§2.8b), and the three languages put it in three places.
  'notConfigured.unset': {
    en: '{variable} was not set when {portal} was built, so it has nowhere to send requests.',
    es: '{variable} no estaba definida cuando se compiló {portal}, así que no tiene adónde enviar peticiones.',
    'pt-BR': '{variable} não foi definida quando {portal} foi compilado, então não há para onde enviar requisições.',
  },
  'notConfigured.rebuild': {
    en: "Rebuild with the API's address — for example:",
    es: 'Vuelve a compilar con la dirección de la API — por ejemplo:',
    'pt-BR': 'Recompile com o endereço da API — por exemplo:',
  },
  'notConfigured.noDefault': {
    en:
      'There is deliberately no default: one pointing at localhost is one ' +
      'somebody deploys and then debugs as an outage.',
    es:
      'Deliberadamente no hay valor predeterminado: uno que apunte a localhost ' +
      'es uno que alguien despliega y luego depura como si fuera una caída.',
    'pt-BR':
      'Deliberadamente não há padrão: um apontando para localhost é um que ' +
      'alguém publica e depois investiga como se fosse uma queda.',
  },

  /* ---------------------------------------------------------------- *
   * Who can sign in (§3.3, §3.7, §3.9)
   * ---------------------------------------------------------------- *
   *
   * The role *codes* stay codes. `roleLabel()` falls back to the code when it
   * knows no word for one, and that fallback is deliberately not routed
   * through here: an unknown role should surface as `FRAUD_ANALYST` in every
   * language rather than as a guess.
   */
  'role.PLATFORM_SUPER_ADMIN': {
    en: 'Super Admin',
    es: 'Superadministrador',
    'pt-BR': 'Superadministrador',
  },
  'role.PLATFORM_SUPPORT': { en: 'Admin', es: 'Administrador', 'pt-BR': 'Administrador' },
  'role.PLATFORM_USER': { en: 'User', es: 'Usuario', 'pt-BR': 'Usuário' },
  'role.OPERATOR_OWNER': { en: 'Owner', es: 'Propietario', 'pt-BR': 'Proprietário' },
  'role.AFFILIATE_DIRECTOR': {
    en: 'Affiliate director',
    es: 'Director de afiliados',
    'pt-BR': 'Diretor de afiliados',
  },
  'role.AFFILIATE_MANAGER': {
    en: 'Affiliate manager',
    es: 'Gestor de afiliados',
    'pt-BR': 'Gerente de afiliados',
  },
  'role.FINANCE': { en: 'Finance', es: 'Finanzas', 'pt-BR': 'Financeiro' },
  // `Compliance` is the word Brazilian business Portuguese uses for this
  // department — it is a loanword in ordinary use there, not a string somebody
  // forgot to translate. Spanish does translate it.
  'role.COMPLIANCE': { en: 'Compliance', es: 'Cumplimiento', 'pt-BR': 'Compliance' },
  'role.FRAUD_ANALYST': {
    en: 'Fraud analyst',
    es: 'Analista de fraude',
    'pt-BR': 'Analista de fraude',
  },
  'role.SUPPORT': { en: 'Support', es: 'Soporte', 'pt-BR': 'Suporte' },

  'staff.state.awaiting': {
    en: 'Awaiting setup',
    es: 'Pendiente de configuración',
    'pt-BR': 'Aguardando configuração',
  },
  'staff.state.awaiting.why': {
    en: 'They have not set up an authenticator app yet, so they cannot sign in.',
    es: 'Todavía no ha configurado una app de autenticación, así que no puede iniciar sesión.',
    'pt-BR': 'Ainda não configurou um app autenticador, então não consegue entrar.',
  },
  'staff.state.awaiting.whyNotEnforced': {
    en:
      'They have not set up an authenticator app. This deployment is not ' +
      'asking for one, so they can sign in without it.',
    es:
      'No ha configurado una app de autenticación. Este despliegue no la exige, ' +
      'así que puede iniciar sesión sin ella.',
    'pt-BR':
      'Não configurou um app autenticador. Esta implantação não exige um, ' +
      'então consegue entrar sem ele.',
  },
  'staff.state.temporary': {
    en: 'Temporary password',
    es: 'Contraseña temporal',
    'pt-BR': 'Senha temporária',
  },
  'staff.state.temporary.why': {
    en: 'Still using the password you set. They are made to change it on first sign-in.',
    es: 'Sigue usando la contraseña que estableciste. Se le obliga a cambiarla en el primer inicio de sesión.',
    'pt-BR': 'Ainda usa a senha que você definiu. É obrigada a trocá-la no primeiro acesso.',
  },
  'staff.state.active': { en: 'Active', es: 'Activa', 'pt-BR': 'Ativa' },

  'staff.field.role': { en: 'Role', es: 'Rol', 'pt-BR': 'Papel' },
  'staff.field.role.hint': {
    en: 'What they will be able to do.',
    es: 'Lo que podrá hacer.',
    'pt-BR': 'O que ela poderá fazer.',
  },
  'staff.field.role.choose': {
    en: 'Choose a role…',
    es: 'Elige un rol…',
    'pt-BR': 'Escolha um papel…',
  },
  'staff.field.tempPassword': {
    en: 'Temporary password',
    es: 'Contraseña temporal',
    'pt-BR': 'Senha temporária',
  },
  'staff.field.tempPassword.hint': {
    en: 'They are made to change it before anything else.',
    es: 'Se le obliga a cambiarla antes que nada.',
    'pt-BR': 'Ela é obrigada a trocá-la antes de qualquer outra coisa.',
  },
  'staff.add': { en: 'Add', es: 'Añadir', 'pt-BR': 'Adicionar' },

  'staff.remove.title': { en: 'Remove {email}', es: 'Eliminar a {email}', 'pt-BR': 'Remover {email}' },
  'staff.remove.alreadyClosed': {
    en: 'This account is already closed and cannot sign in. Permanent delete removes the row itself.',
    es: 'Esta cuenta ya está cerrada y no puede iniciar sesión. La eliminación permanente borra el registro en sí.',
    'pt-BR': 'Esta conta já está fechada e não consegue entrar. A exclusão permanente remove o próprio registro.',
  },
  'staff.remove.permanent': {
    en:
      "Permanent delete removes this person's account, credentials, MFA " +
      'enrolment and role grants outright. This cannot be undone. Every ' +
      'record they created or acted on elsewhere — deals, invoices, disputes ' +
      'and the like — keeps their name on it; only the account itself is gone.',
    es:
      'La eliminación permanente borra por completo la cuenta de esta persona, ' +
      'sus credenciales, su segundo factor y sus permisos. No se puede deshacer. ' +
      'Cada registro que creó o en el que actuó — acuerdos, facturas, disputas y ' +
      'similares — conserva su nombre; solo desaparece la cuenta en sí.',
    'pt-BR':
      'A exclusão permanente remove a conta desta pessoa, suas credenciais, seu ' +
      'segundo fator e suas permissões de vez. Isso não pode ser desfeito. Todo ' +
      'registro que ela criou ou em que atuou — acordos, faturas, contestações e ' +
      'afins — mantém o nome dela; só a conta em si desaparece.',
  },
  'staff.remove.reversible': {
    en:
      'Closes the account. They can no longer sign in — every request is ' +
      'checked against this on the way in, so nothing else has to be ' +
      'separately revoked. This is the reversible option.',
    es:
      'Cierra la cuenta. Ya no podrá iniciar sesión: cada petición se comprueba ' +
      'contra esto a la entrada, así que no hay que revocar nada más por ' +
      'separado. Esta es la opción reversible.',
    'pt-BR':
      'Fecha a conta. Ela não consegue mais entrar — cada requisição é conferida ' +
      'contra isso na entrada, então nada mais precisa ser revogado à parte. ' +
      'Esta é a opção reversível.',
  },
  'staff.remove.confirmField': {
    en: 'Type their exact email to confirm',
    es: 'Escribe su correo exacto para confirmar',
    'pt-BR': 'Digite o e-mail exato dela para confirmar',
  },
  'staff.remove.permanentCheckbox': {
    en: 'Delete permanently — remove the account itself (cannot be undone)',
    es: 'Eliminar permanentemente — borrar la cuenta en sí (no se puede deshacer)',
    'pt-BR': 'Excluir permanentemente — remover a própria conta (não pode ser desfeito)',
  },
  'staff.remove.permanentAction': {
    en: 'Delete permanently',
    es: 'Eliminar permanentemente',
    'pt-BR': 'Excluir permanentemente',
  },
  'staff.remove.action': { en: 'Remove', es: 'Eliminar', 'pt-BR': 'Remover' },

  'staff.facts.roles': { en: 'Roles', es: 'Roles', 'pt-BR': 'Papéis' },
  'staff.facts.roles.none': { en: 'None yet', es: 'Ninguno todavía', 'pt-BR': 'Nenhum ainda' },
  'staff.facts.state': { en: 'State', es: 'Estado', 'pt-BR': 'Estado' },
  'staff.facts.authenticator': {
    en: 'Authenticator app',
    es: 'App de autenticación',
    'pt-BR': 'App autenticador',
  },
  'staff.facts.authenticator.set': { en: 'Set up', es: 'Configurada', 'pt-BR': 'Configurado' },
  'staff.facts.authenticator.unset': {
    en: 'Not set up yet',
    es: 'Todavía sin configurar',
    'pt-BR': 'Ainda não configurado',
  },
  'staff.facts.authenticator.notAsked': {
    en: 'Not set up, and not asked for here',
    es: 'Sin configurar, y aquí no se exige',
    'pt-BR': 'Não configurado, e não é exigido aqui',
  },
  'staff.facts.password.temporary': {
    en: 'Still the temporary one you set',
    es: 'Sigue siendo la temporal que estableciste',
    'pt-BR': 'Ainda é a temporária que você definiu',
  },
  'staff.facts.password.own': { en: 'Their own', es: 'La suya', 'pt-BR': 'A dela' },

  'staff.reset.warning': {
    en:
      'Clears the authenticator app on this account. They will be asked to set ' +
      'up a new one the next time they sign in, and cannot sign in until they ' +
      'have — this hands the account back, it does not switch the second factor ' +
      'off. Their password is unchanged.',
    es:
      'Borra la app de autenticación de esta cuenta. Se le pedirá configurar una ' +
      'nueva la próxima vez que inicie sesión, y no podrá entrar hasta hacerlo: ' +
      'esto devuelve la cuenta, no desactiva el segundo factor. Su contraseña no ' +
      'cambia.',
    'pt-BR':
      'Limpa o app autenticador desta conta. Ela será solicitada a configurar um ' +
      'novo no próximo acesso, e não consegue entrar até fazer isso — isto ' +
      'devolve a conta, não desliga o segundo fator. A senha dela não muda.',
  },
  'staff.reset.busy': { en: 'Resetting…', es: 'Restableciendo…', 'pt-BR': 'Redefinindo…' },
  'staff.reset.confirm': {
    en: 'Reset authenticator',
    es: 'Restablecer',
    'pt-BR': 'Redefinir',
  },
  'staff.reset.action': {
    en: 'Reset authenticator app',
    es: 'Restablecer la app de autenticación',
    'pt-BR': 'Redefinir o app autenticador',
  },

  'staff.loadFailed': {
    en: 'Could not load the staff list.',
    es: 'No se pudo cargar la lista de personas.',
    'pt-BR': 'Não foi possível carregar a lista de pessoas.',
  },
  'staff.title.platform': { en: 'Users', es: 'Usuarios', 'pt-BR': 'Usuários' },
  'staff.title.operator': { en: 'Your team', es: 'Tu equipo', 'pt-BR': 'Sua equipe' },
  'staff.addPerson.platform': {
    en: 'Add user',
    es: 'Añadir usuario',
    'pt-BR': 'Adicionar usuário',
  },
  'staff.addPerson.operator': {
    en: 'Add team member',
    es: 'Añadir a alguien del equipo',
    'pt-BR': 'Adicionar alguém da equipe',
  },
  'staff.search.placeholder': { en: 'Email or role', es: 'Correo o rol', 'pt-BR': 'E-mail ou papel' },
  'staff.panel': { en: 'Who can sign in', es: 'Quién puede iniciar sesión', 'pt-BR': 'Quem pode entrar' },
  'staff.empty.query': { en: 'Nobody matches that.', es: 'No hay nadie que coincida.', 'pt-BR': 'Ninguém corresponde a isso.' },
  'staff.empty': { en: 'Nobody yet.', es: 'Todavía no hay nadie.', 'pt-BR': 'Ainda não há ninguém.' },
  'staff.self': { en: 'You', es: 'Tú', 'pt-BR': 'Você' },
  /* Why there is no Remove on the owner's row. Not "You" — an owner is
     protected from everybody, including the other administrators looking at
     this list. */
  'staff.owner.locked': { en: 'Owner', es: 'Propietario', 'pt-BR': 'Proprietário' },
  'staff.facts.brands': { en: 'Brands', es: 'Marcas', 'pt-BR': 'Marcas' },
  'staff.brands.all': { en: 'All brands', es: 'Todas las marcas', 'pt-BR': 'Todas as marcas' },
  'staff.brands.owner': { en: 'All brands (owner)', es: 'Todas las marcas (propietario)', 'pt-BR': 'Todas as marcas (proprietário)' },
  'staff.brands.field': { en: 'Limit to brands', es: 'Limitar a marcas', 'pt-BR': 'Limitar a marcas' },
  'staff.brands.field.hint': {
    en: 'Tick none and they act on every brand. Their roles say what they may do; this says only where.',
    es: 'Sin marcar ninguna, actúan en todas las marcas. Sus roles dicen qué pueden hacer; esto solo dice dónde.',
    'pt-BR': 'Sem marcar nenhuma, atuam em todas as marcas. Os papéis dizem o que podem fazer; isto diz apenas onde.',
  },
  'staff.brands.pickerAll': { en: 'Every brand', es: 'Todas las marcas', 'pt-BR': 'Todas as marcas' },
  'staff.brands.pickerSome': { en: 'Limited to {n} of them', es: 'Limitado a {n} de ellas', 'pt-BR': 'Limitado a {n} delas' },
  'staff.brands.lockedNote': {
    en: 'Added from inside {brand}, this person will be limited to {brand}. Widen that from the operator console.',
    es: 'Añadida desde {brand}, esta persona quedará limitada a {brand}. Amplíalo desde la consola del operador.',
    'pt-BR': 'Adicionada de dentro de {brand}, esta pessoa ficará limitada a {brand}. Amplie isso no console do operador.',
  },
  'staff.brands.save': { en: 'Save brands', es: 'Guardar marcas', 'pt-BR': 'Salvar marcas' },
  'staff.brands.saved': { en: 'Brands saved.', es: 'Marcas guardadas.', 'pt-BR': 'Marcas salvas.' },
  'staff.combinations': {
    en:
      'Some role combinations are refused outright. Setting commission terms ' +
      'and recording payment against them is one person deciding what is owed ' +
      'and then declaring it settled — that pair cannot sit in one role, and ' +
      'adding it is refused when you save rather than found in an audit ' +
      'afterwards.',
    es:
      'Algunas combinaciones de roles se rechazan de plano. Fijar las condiciones ' +
      'de comisión y registrar el pago correspondiente es que una misma persona ' +
      'decida lo que se debe y luego lo declare saldado: esa pareja no puede ' +
      'estar en un solo rol, y añadirla se rechaza al guardar en lugar de ' +
      'descubrirse después en una auditoría.',
    'pt-BR':
      'Algumas combinações de papéis são recusadas de saída. Definir as condições ' +
      'de comissão e registrar o pagamento correspondente é uma mesma pessoa ' +
      'decidir o que é devido e depois declarar quitado — esse par não cabe em um ' +
      'só papel, e adicioná-lo é recusado ao salvar, em vez de ser descoberto ' +
      'depois numa auditoria.',
  },
  'ui.noMatches': { en: 'No matches.', es: 'No hay coincidencias.', 'pt-BR': 'Nenhum resultado.' },
  'ui.date.choose': { en: 'Choose a date', es: 'Elige una fecha', 'pt-BR': 'Escolha uma data' },
  'ui.date.calendar': { en: 'Calendar', es: 'Calendario', 'pt-BR': 'Calendário' },
  'ui.date.previousMonth': { en: 'Previous month', es: 'Mes anterior', 'pt-BR': 'Mês anterior' },
  'ui.date.nextMonth': { en: 'Next month', es: 'Mes siguiente', 'pt-BR': 'Próximo mês' },
  'ui.date.today': { en: 'Today', es: 'Hoy', 'pt-BR': 'Hoje' },
  'ui.date.clear': { en: 'Clear', es: 'Borrar', 'pt-BR': 'Limpar' },
  'ui.dialog.ok': { en: 'OK', es: 'Aceptar', 'pt-BR': 'OK' },
  'ui.dialog.cancel': { en: 'Cancel', es: 'Cancelar', 'pt-BR': 'Cancelar' },
  'ui.dialog.moreChars.one': { en: '1 more character needed.', es: 'Falta 1 carácter.', 'pt-BR': 'Falta 1 caractere.' },
  'ui.dialog.moreChars.other': { en: '{n} more characters needed.', es: 'Faltan {n} caracteres.', 'pt-BR': 'Faltam {n} caracteres.' },
  'ui.dialog.confirmTitle': { en: 'Are you sure?', es: '¿Seguro?', 'pt-BR': 'Tem certeza?' },

  /* ---------------------------------------------------------------- *
   * Paging, exports and disclosure — the three things the kit had no
   * component for, so no screen had them (audit, 2026-09-28).
   * ---------------------------------------------------------------- */
  'ui.pager.more': { en: 'Show more', es: 'Mostrar más', 'pt-BR': 'Mostrar mais' },
  'ui.pager.loading': { en: 'Loading…', es: 'Cargando…', 'pt-BR': 'Carregando…' },
  // Never a total: these endpoints page by cursor and do not count the rest,
  // and "1–50 of 4,812" with a made-up 4,812 is worse than not saying.
  'ui.pager.shown': { en: 'Showing {n}', es: 'Mostrando {n}', 'pt-BR': 'Mostrando {n}' },
  'ui.pager.all': { en: 'Showing all {n}', es: 'Mostrando los {n}', 'pt-BR': 'Mostrando todos os {n}' },
  'ui.pager.ceiling': {
    en: 'This is as much as a report will return — narrow the filters to see the rest.',
    es: 'Esto es todo lo que devuelve un informe: acota los filtros para ver el resto.',
    'pt-BR': 'Isto é tudo o que um relatório devolve — restrinja os filtros para ver o resto.',
  },
  'ui.export.csv': { en: 'CSV', es: 'CSV', 'pt-BR': 'CSV' },
  'ui.export.xml': { en: 'XML', es: 'XML', 'pt-BR': 'XML' },
  'ui.export.xls': { en: 'XLS', es: 'XLS', 'pt-BR': 'XLS' },
  'ui.export.label': { en: 'Download', es: 'Descargar', 'pt-BR': 'Baixar' },
  'ui.disclosure.show': { en: 'Show', es: 'Mostrar', 'pt-BR': 'Mostrar' },
  'ui.disclosure.hide': { en: 'Hide', es: 'Ocultar', 'pt-BR': 'Ocultar' },

  /* ---------------------------------------------------------------- *
   * The shell in `shell.tsx` — the rail every console shares
   * ---------------------------------------------------------------- */
  'shell.menu.open': { en: 'Open menu', es: 'Abrir menú', 'pt-BR': 'Abrir menu' },
  // Read after a rail or section label that carries the unseen-activity dot.
  'shell.newActivity': { en: 'New activity', es: 'Actividad nueva', 'pt-BR': 'Nova atividade' },
  'shell.menu.close': { en: 'Close menu', es: 'Cerrar menú', 'pt-BR': 'Fechar menu' },
  'shell.theme': { en: 'Theme', es: 'Tema', 'pt-BR': 'Tema' },
  'shell.theme.system': { en: 'Match system', es: 'Según el sistema', 'pt-BR': 'Igual ao sistema' },
  'shell.theme.light': { en: 'Light', es: 'Claro', 'pt-BR': 'Claro' },
  'shell.theme.dark': { en: 'Dark', es: 'Oscuro', 'pt-BR': 'Escuro' },

  /* ---------------------------------------------------------------- *
   * The intro tour (`Tour.tsx`) — the controls; each console writes its
   * own steps
   * ---------------------------------------------------------------- */
  'tour.open': { en: 'Platform tour', es: 'Recorrido por la plataforma', 'pt-BR': 'Tour pela plataforma' },
  // Read after the rail button's name while a newer tour waits to be opened.
  'tour.newSteps': { en: 'New steps added', es: 'Hay pasos nuevos', 'pt-BR': 'Há etapas novas' },
  'tour.new': { en: 'New', es: 'Nuevo', 'pt-BR': 'Novo' },
  'tour.progress': {
    en: 'Step {n} of {total}',
    es: 'Paso {n} de {total}',
    'pt-BR': 'Etapa {n} de {total}',
  },
  'tour.skip': { en: 'Skip tour', es: 'Omitir recorrido', 'pt-BR': 'Pular tour' },
  'tour.exit': { en: 'Exit tour', es: 'Salir del recorrido', 'pt-BR': 'Sair do tour' },
  'tour.back': { en: 'Back', es: 'Atrás', 'pt-BR': 'Voltar' },
  'tour.next': { en: 'Next', es: 'Siguiente', 'pt-BR': 'Próximo' },
  'tour.done': { en: 'Done', es: 'Listo', 'pt-BR': 'Concluir' },
  'tour.saveFailed': {
    en:
      'We could not record that you have seen the tour, so it may open again ' +
      'the next time you sign in. You can carry on as normal.',
    es:
      'No pudimos registrar que ya viste el recorrido, así que puede volver a ' +
      'abrirse la próxima vez que inicies sesión. Puedes seguir con normalidad.',
    'pt-BR':
      'Não conseguimos registrar que você já viu o tour, então ele pode abrir ' +
      'de novo na próxima vez que você entrar. Você pode continuar normalmente.',
  },

  /* ---------------------------------------------------------------- *
   * §12.4 — `AccentPicker` (accent.ts), a brand's own colour
   * ---------------------------------------------------------------- */
  'accent.picker.group': { en: 'Colour', es: 'Color', 'pt-BR': 'Cor' },
  'accent.picker.standard': {
    en: 'Standard colour',
    es: 'Color estándar',
    'pt-BR': 'Cor padrão',
  },
  'accent.picker.custom.swatch': {
    en: 'Pick a custom colour',
    es: 'Elegir un color personalizado',
    'pt-BR': 'Escolher uma cor personalizada',
  },
  'accent.picker.hex': { en: 'Hex code', es: 'Código hexadecimal', 'pt-BR': 'Código hexadecimal' },
  'accent.picker.hex.hint': {
    en: 'Six digits, e.g. #ad2b7c',
    es: 'Seis dígitos, p. ej. #ad2b7c',
    'pt-BR': 'Seis dígitos, ex.: #ad2b7c',
  },
  'accent.picker.hex.invalid': {
    en: 'Not a colour: six digits after the #.',
    es: 'No es un color válido: seis dígitos después del #.',
    'pt-BR': 'Não é uma cor válida: seis dígitos após o #.',
  },
  'accent.picker.custom': { en: 'Custom', es: 'Personalizado', 'pt-BR': 'Personalizada' },
  'accent.picker.preview.light': { en: 'Light theme', es: 'Tema claro', 'pt-BR': 'Tema claro' },
  'accent.picker.preview.dark': { en: 'Dark theme', es: 'Tema oscuro', 'pt-BR': 'Tema escuro' },
  'accent.picker.adjustedTo': {
    en: 'Shown as {hex} so text stays readable',
    es: 'Se muestra como {hex} para que el texto siga legible',
    'pt-BR': 'Mostrada como {hex} para o texto continuar legível',
  },
  'accent.picker.clash': {
    en:
      'Close to the colour used for overdue/settled figures; those figures ' +
      'will still show their shape.',
    es:
      'Es cercano al color usado para las cifras vencidas/liquidadas; esas ' +
      'cifras seguirán mostrando su forma.',
    'pt-BR':
      'Próximo da cor usada para valores vencidos/quitados; esses valores ' +
      'continuarão mostrando sua forma.',
  },
  'accent.picker.preview.action': { en: 'Action', es: 'Acción', 'pt-BR': 'Ação' },
  'accent.picker.preview.provisional': {
    en: 'Provisional',
    es: 'Provisional',
    'pt-BR': 'Provisório',
  },
  'accent.preset.slate': { en: 'Slate', es: 'Pizarra', 'pt-BR': 'Ardósia' },
  'accent.preset.ruby': { en: 'Ruby', es: 'Rubí', 'pt-BR': 'Rubi' },
  'accent.preset.ember': { en: 'Ember', es: 'Ascua', 'pt-BR': 'Brasa' },
  'accent.preset.gold': { en: 'Gold', es: 'Oro', 'pt-BR': 'Ouro' },
  'accent.preset.olive': { en: 'Olive', es: 'Oliva', 'pt-BR': 'Oliva' },
  'accent.preset.teal': { en: 'Teal', es: 'Verde azulado', 'pt-BR': 'Verde-azulado' },
  'accent.preset.ocean': { en: 'Ocean', es: 'Océano', 'pt-BR': 'Oceano' },
  'accent.preset.indigo': { en: 'Indigo', es: 'Índigo', 'pt-BR': 'Índigo' },
  // Rule Book 35.3a — the cohort report, shared by every console.
  'cohort.title': { en: 'Cohorts', es: 'Cohortes', 'pt-BR': 'Coortes' },
  'cohort.anchor': { en: 'Cohort by', es: 'Cohorte por', 'pt-BR': 'Coorte por' },
  'cohort.anchor.REGISTRATION': { en: 'Registration', es: 'Registro', 'pt-BR': 'Cadastro' },
  'cohort.anchor.FTD': { en: 'First deposit', es: 'Primer depósito', 'pt-BR': 'Primeiro depósito' },
  'cohort.granularity': { en: 'Checkpoints', es: 'Puntos de control', 'pt-BR': 'Marcos' },
  'cohort.granularity.MONTHS': { en: 'Months', es: 'Meses', 'pt-BR': 'Meses' },
  'cohort.granularity.DAYS': { en: 'Days', es: 'Días', 'pt-BR': 'Dias' },
  'cohort.period': { en: 'Cohort period', es: 'Periodo de cohorte', 'pt-BR': 'Período da coorte' },
  'cohort.period.MONTH': { en: 'Month', es: 'Mes', 'pt-BR': 'Mês' },
  'cohort.period.WEEK': { en: 'Week', es: 'Semana', 'pt-BR': 'Semana' },
  'cohort.dimension': { en: 'Split by', es: 'Dividir por', 'pt-BR': 'Dividir por' },
  'cohort.dim.none': { en: 'Nothing', es: 'Nada', 'pt-BR': 'Nada' },
  'cohort.dim.affiliate': { en: 'Affiliate', es: 'Afiliado', 'pt-BR': 'Afiliado' },
  'cohort.dim.campaign': { en: 'Campaign', es: 'Campaña', 'pt-BR': 'Campanha' },
  'cohort.dim.brand': { en: 'Brand', es: 'Marca', 'pt-BR': 'Marca' },
  'cohort.dim.product': { en: 'Product', es: 'Producto', 'pt-BR': 'Produto' },
  'cohort.dim.country': { en: 'Country', es: 'País', 'pt-BR': 'País' },
  'cohort.dim.subId': { en: 'Sub-ID {n}', es: 'Sub-ID {n}', 'pt-BR': 'Sub-ID {n}' },
  'cohort.metric': { en: 'Show', es: 'Mostrar', 'pt-BR': 'Mostrar' },
  'cohort.metric.retention': { en: 'Retention', es: 'Retención', 'pt-BR': 'Retenção' },
  'cohort.metric.active': { en: 'Active players', es: 'Jugadores activos', 'pt-BR': 'Jogadores ativos' },
  'cohort.metric.players': { en: 'Cohort size', es: 'Tamaño de la cohorte', 'pt-BR': 'Tamanho da coorte' },
  'cohort.metric.ftds': { en: 'First deposits', es: 'Primeros depósitos', 'pt-BR': 'Primeiros depósitos' },
  'cohort.metric.depositors': { en: 'Depositors', es: 'Depositantes', 'pt-BR': 'Depositantes' },
  'cohort.metric.repeat': { en: 'Repeat depositors', es: 'Depositantes recurrentes', 'pt-BR': 'Depositantes recorrentes' },
  'cohort.metric.depositCount': { en: 'Deposit count', es: 'Número de depósitos', 'pt-BR': 'Número de depósitos' },
  'cohort.metric.deposits': { en: 'Deposits', es: 'Depósitos', 'pt-BR': 'Depósitos' },
  'cohort.metric.withdrawals': { en: 'Withdrawals', es: 'Retiros', 'pt-BR': 'Saques' },
  'cohort.metric.ggr': { en: 'GGR', es: 'GGR', 'pt-BR': 'GGR' },
  'cohort.metric.bonus': { en: 'Bonus cost', es: 'Coste de bonos', 'pt-BR': 'Custo de bônus' },
  'cohort.metric.ngr': { en: 'GGR minus bonus', es: 'GGR menos bonos', 'pt-BR': 'GGR menos bônus' },
  'cohort.metric.commission': { en: 'Commission earned', es: 'Comisión ganada', 'pt-BR': 'Comissão ganha' },
  'cohort.metric.ngrPerPlayer': { en: 'GGR minus bonus per player', es: 'GGR menos bonos por jugador', 'pt-BR': 'GGR menos bônus por jogador' },
  'cohort.currency': { en: 'Currency', es: 'Moneda', 'pt-BR': 'Moeda' },
  'cohort.col.cohort': { en: 'Cohort', es: 'Cohorte', 'pt-BR': 'Coorte' },
  'cohort.col.group': { en: 'Group', es: 'Grupo', 'pt-BR': 'Grupo' },
  'cohort.col.players': { en: 'Players', es: 'Jugadores', 'pt-BR': 'Jogadores' },
  'cohort.subIdFilter': { en: 'Only sub-ID', es: 'Solo sub-ID', 'pt-BR': 'Apenas sub-ID' },
  'cohort.subIdValue': { en: 'Sub-ID value', es: 'Valor de sub-ID', 'pt-BR': 'Valor do sub-ID' },
  'cohort.unset': { en: '(not set)', es: '(sin definir)', 'pt-BR': '(não definido)' },
  'cohort.pooled': { en: 'Other (small cohorts)', es: 'Otras (cohortes pequeñas)', 'pt-BR': 'Outras (coortes pequenas)' },
  'cohort.withheld': { en: 'Insufficient cohort size', es: 'Tamaño de cohorte insuficiente', 'pt-BR': 'Tamanho de coorte insuficiente' },
  'cohort.weekOf': { en: 'Week of {d}', es: 'Semana del {d}', 'pt-BR': 'Semana de {d}' },
  'cohort.incomplete': { en: 'Not complete yet: {n} of {total} players have reached this checkpoint.', es: 'Aún no completo: {n} de {total} jugadores han llegado a este punto.', 'pt-BR': 'Ainda incompleto: {n} de {total} jogadores chegaram a este marco.' },
  'cohort.incompleteNote': { en: 'A faded cell is a checkpoint some players have not reached yet, or whose last day the brand has not finished reporting. Its figures are so far, and retention is not given until it is complete.', es: 'Una celda atenuada es un punto que algunos jugadores aún no han alcanzado, o cuyo último día la marca aún no ha terminado de informar. Sus cifras son parciales y la retención no se da hasta que se complete.', 'pt-BR': 'Uma célula esmaecida é um marco que alguns jogadores ainda não alcançaram, ou cujo último dia a marca ainda não terminou de informar. Os valores são parciais e a retenção só aparece quando estiver completo.' },
  'cohort.activityNote': { en: 'Each checkpoint counts from the player\'s own registration or first deposit. Active means the brand reported wagering on a day inside that checkpoint\'s own window; a deposit alone does not count.', es: 'Cada punto cuenta desde el registro o primer depósito del propio jugador. Activo significa que la marca informó apuestas en un día dentro de la ventana de ese punto; un depósito por sí solo no cuenta.', 'pt-BR': 'Cada marco conta a partir do cadastro ou primeiro depósito do próprio jogador. Ativo significa que a marca informou apostas num dia dentro da janela desse marco; um depósito sozinho não conta.' },
  'cohort.empty': { en: 'No player falls in a cohort in this range.', es: 'Ningún jugador entra en una cohorte en este periodo.', 'pt-BR': 'Nenhum jogador entra numa coorte neste período.' },
  'cohort.drillHint': { en: 'Select a cell to see the players behind it.', es: 'Seleccione una celda para ver los jugadores.', 'pt-BR': 'Selecione uma célula para ver os jogadores.' },
  'cohort.drill.title': { en: 'Players: {cohort}, {checkpoint}', es: 'Jugadores: {cohort}, {checkpoint}', 'pt-BR': 'Jogadores: {cohort}, {checkpoint}' },
  'cohort.drill.close': { en: 'Close', es: 'Cerrar', 'pt-BR': 'Fechar' },
  'cohort.drill.more': { en: 'Showing the first 500 players.', es: 'Se muestran los primeros 500 jugadores.', 'pt-BR': 'Mostrando os primeiros 500 jogadores.' },
  'cohort.col.player': { en: 'Player', es: 'Jugador', 'pt-BR': 'Jogador' },
  'cohort.col.registered': { en: 'Registered', es: 'Registrado', 'pt-BR': 'Cadastrado' },
  'cohort.col.firstDeposit': { en: 'First deposit', es: 'Primer depósito', 'pt-BR': 'Primeiro depósito' },
  'cohort.col.country': { en: 'Country', es: 'País', 'pt-BR': 'País' },
  'cohort.col.active': { en: 'Active', es: 'Activo', 'pt-BR': 'Ativo' },
  'cohort.yes': { en: 'Yes', es: 'Sí', 'pt-BR': 'Sim' },
  'cohort.no': { en: 'No', es: 'No', 'pt-BR': 'Não' },
  'cohort.otherCurrencyLines': { en: '{n} commission lines priced in another currency are counted apart, not added.', es: '{n} líneas de comisión en otra moneda se cuentan aparte, no se suman.', 'pt-BR': '{n} linhas de comissão em outra moeda são contadas à parte, não somadas.' },
  'loginAs.banner': {
    en: 'AffDice SuperAdmin: {who} is signed in as you. Reason: {reason}. Ends at {ends}.',
    es: 'AffDice SuperAdmin: {who} ha iniciado sesión como usted. Motivo: {reason}. Termina a las {ends}.',
    'pt-BR': 'AffDice SuperAdmin: {who} entrou como você. Motivo: {reason}. Termina às {ends}.',
  },
  'loginAs.end': { en: 'End session', es: 'Terminar sesión', 'pt-BR': 'Encerrar sessão' },
  'loginAs.ending': { en: 'Ending…', es: 'Terminando…', 'pt-BR': 'Encerrando…' },
  'loginAs.endFailed': {
    en: 'The session could not be ended. Try again, or close this tab: it ends by itself at the time shown.',
    es: 'No se pudo terminar la sesión. Inténtelo de nuevo o cierre esta pestaña: termina sola a la hora indicada.',
    'pt-BR': 'Não foi possível encerrar a sessão. Tente de novo ou feche esta aba: ela termina sozinha no horário indicado.',
  },
  'loginAs.ended': {
    en: 'The AffDice SuperAdmin session has ended. You can close this tab.',
    es: 'La sesión de AffDice SuperAdmin ha terminado. Puede cerrar esta pestaña.',
    'pt-BR': 'A sessão do AffDice SuperAdmin terminou. Você pode fechar esta aba.',
  },
  'loginAs.opening': { en: 'Signing in from AffDice SuperAdmin…', es: 'Iniciando sesión desde AffDice SuperAdmin…', 'pt-BR': 'Entrando pelo AffDice SuperAdmin…' },
  'loginAs.linkUsed': {
    en: 'This sign-in link has been used or has expired. Start the login again from AffDice SuperAdmin.',
    es: 'Este enlace de inicio de sesión ya se usó o ha caducado. Vuelva a iniciar el acceso desde AffDice SuperAdmin.',
    'pt-BR': 'Este link de acesso já foi usado ou expirou. Inicie o acesso de novo pelo AffDice SuperAdmin.',
  },
  /* ---------------------------------------------------------------- *
   * LogoUpload / PortalMark — a brand's logo
   * ---------------------------------------------------------------- */
  'logo.label': { en: 'Logo', es: 'Logotipo', 'pt-BR': 'Logotipo' },
  'logo.hint': {
    en: 'PNG, JPEG or WebP, up to 1 MB. Shown in the rail, on lists and on the brand sign-in page.',
    es: 'PNG, JPEG o WebP, hasta 1 MB. Se muestra en el menú, en las listas y en la página de acceso de la marca.',
    'pt-BR': 'PNG, JPEG ou WebP, até 1 MB. Aparece no menu, nas listas e na página de acesso da marca.',
  },
  'logo.preview': { en: 'Logo preview', es: 'Vista previa del logotipo', 'pt-BR': 'Pré-visualização do logotipo' },
  'logo.none': { en: 'No logo yet. The brand colour tile is used.', es: 'Aún sin logotipo. Se usa el icono con el color de la marca.', 'pt-BR': 'Ainda sem logotipo. Usa-se o ícone com a cor da marca.' },
  'logo.choose': { en: 'Choose a file', es: 'Elegir un archivo', 'pt-BR': 'Escolher um arquivo' },
  'logo.replace': { en: 'Replace', es: 'Reemplazar', 'pt-BR': 'Substituir' },
  'logo.remove': { en: 'Remove', es: 'Quitar', 'pt-BR': 'Remover' },
  'logo.removing': { en: 'Removing…', es: 'Quitando…', 'pt-BR': 'Removendo…' },
  'logo.save': { en: 'Save logo', es: 'Guardar logotipo', 'pt-BR': 'Salvar logotipo' },
  'logo.uploading': { en: 'Uploading…', es: 'Subiendo…', 'pt-BR': 'Enviando…' },
  'logo.discard': { en: 'Discard', es: 'Descartar', 'pt-BR': 'Descartar' },
  'logo.saved': { en: 'Logo saved.', es: 'Logotipo guardado.', 'pt-BR': 'Logotipo salvo.' },
  'logo.removed': { en: 'Logo removed.', es: 'Logotipo quitado.', 'pt-BR': 'Logotipo removido.' },
  'logo.staged': { en: 'Not saved yet: {name}', es: 'Sin guardar: {name}', 'pt-BR': 'Ainda não salvo: {name}' },
  'logo.error.type': {
    en: 'Use a PNG, JPEG or WebP image. SVG files are not accepted.',
    es: 'Usa una imagen PNG, JPEG o WebP. No se aceptan archivos SVG.',
    'pt-BR': 'Use uma imagem PNG, JPEG ou WebP. Arquivos SVG não são aceitos.',
  },
  'logo.error.size': {
    en: 'That file is over 1 MB. Choose a smaller image.',
    es: 'Ese archivo supera 1 MB. Elige una imagen más pequeña.',
    'pt-BR': 'Esse arquivo passa de 1 MB. Escolha uma imagem menor.',
  },
  'logo.error.empty': {
    en: 'That file is empty.',
    es: 'Ese archivo está vacío.',
    'pt-BR': 'Esse arquivo está vazio.',
  },
  'logo.error.notWhatItSays': {
    en: 'That file is not really the image type its name says. Export it again as a PNG, JPEG or WebP.',
    es: 'Ese archivo no es realmente del tipo de imagen que indica su nombre. Expórtalo de nuevo como PNG, JPEG o WebP.',
    'pt-BR': 'Esse arquivo não é realmente do tipo de imagem que o nome indica. Exporte de novo como PNG, JPEG ou WebP.',
  },
  'logo.error.unreadable': {
    en: 'That image could not be read. Try another file.',
    es: 'No se pudo leer esa imagen. Prueba con otro archivo.',
    'pt-BR': 'Não foi possível ler essa imagem. Tente outro arquivo.',
  },
  'logo.error.rendition': {
    en: 'Your browser could not prepare this image. Try another file, or another browser.',
    es: 'Tu navegador no pudo preparar esta imagen. Prueba con otro archivo u otro navegador.',
    'pt-BR': 'Seu navegador não conseguiu preparar esta imagem. Tente outro arquivo ou outro navegador.',
  },
  'logo.error.failed': {
    en: 'The logo could not be saved. Try again.',
    es: 'No se pudo guardar el logotipo. Inténtalo de nuevo.',
    'pt-BR': 'Não foi possível salvar o logotipo. Tente novamente.',
  },
  'logo.alt': { en: '{brand} logo', es: 'Logotipo de {brand}', 'pt-BR': 'Logotipo de {brand}' },
} as const satisfies Catalogue;

export type KitMessageKey = keyof typeof KIT_MESSAGES;

/*
 * Built once per locale rather than per call: `freshness()` runs on every
 * render of every dashboard, and rebuilding a fallback chain for it is work
 * that produces the same three tags every time.
 */
const cache = new Map<string, Translator<typeof KIT_MESSAGES>>();

/** The kit's own words, in whatever language this tab is reading (§2.8d does the rest). */
export function kitText(key: KitMessageKey, params?: Params): string {
  return tabTranslator(KIT_MESSAGES, cache)(key, params);
}
